import { useState } from 'react';
import api from '@/services/api';
import { storageUrl } from '@/services/config';

const statusMap: Record<string, { label: string; color: string }> = {
  pending: { label: 'قيد الانتظار', color: 'bg-gray-100 text-gray-800' },
  processing: { label: 'قيد التجهيز', color: 'bg-yellow-100 text-yellow-800' },
  processed: { label: 'تم التجهيز', color: 'bg-blue-100 text-blue-800' },
  delivery: { label: 'في الطريق', color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'تم التسليم', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-800' },
};

const paymentLabels: Record<string, string> = { pending: 'معلق', paid: 'مدفوع', failed: 'فشل' };
const paymentColors: Record<string, string> = { pending: 'bg-yellow-100 text-yellow-800', paid: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800' };
const methodLabels: Record<string, string> = { cash: 'الدفع عند الاستلام', credit_card: 'بطاقة ائتمان', gift_card: 'بطاقة هدية' };

export default function TrackOrderPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim()) return;
    setError('');
    setOrder(null);
    setLoading(true);
    try {
      const res = await api.get(`/track-order/${orderNumber.trim()}`);
      setOrder(res.data);
    } catch (err: any) {
      setError(err.response?.status === 404 ? 'الطلب غير موجود. تأكدي من رقم الطلب.' : 'حدث خطأ، حاولي مرة أخرى');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-cream-50">
      <div className="bg-cream-200 py-12 md:py-16">
        <div className="container-main text-center">
          <h1 className="heading-xl mb-3">تتبع الطلب</h1>
          <p className="body-text">أدخلي رقم الطلب لمعرفة حالة طلبك</p>
              </div>
            </div>

      <div className="container-main py-8">
        {/* Search Form */}
        <div className="max-w-md mx-auto mb-8">
          <form onSubmit={handleTrack} className="flex gap-3">
            <input
              type="text"
              value={orderNumber}
              onChange={e => setOrderNumber(e.target.value.toUpperCase())}
              placeholder="مثال: ORD-2026-001"
              dir="ltr"
              className="flex-1 border border-cream-300 rounded-xl px-4 py-3 text-sm font-mono text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:border-blush-200 bg-white"
            />
            <button type="submit" disabled={loading} className="px-6 py-3 bg-charcoal-900 text-white rounded-xl text-sm font-medium hover:bg-charcoal-800 disabled:opacity-50 whitespace-nowrap">
              {loading ? 'جاري البحث...' : 'تتبع'}
            </button>
          </form>
          {error && <p className="text-red-600 text-sm mt-3 text-right">{error}</p>}
        </div>

        {/* Order Details */}
        {order && (
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl border border-cream-300 shadow-sm p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <p className="text-xs text-charcoal-400 mb-1">رقم الطلب</p>
                  <p className="text-lg font-mono font-bold text-charcoal-900">{order.order_number}</p>
                  <p className="text-xs text-charcoal-400 mt-1">{(() => { const d = new Date(order.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}</p>
                </div>
                <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${statusMap[order.status]?.color || ''}`}>
                  {statusMap[order.status]?.label || order.status}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-cream-100">
                <div>
                  <p className="text-xs text-charcoal-400 mb-1">حالة الدفع</p>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${paymentColors[order.payment_status] || ''}`}>
                    {paymentLabels[order.payment_status] || order.payment_status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-charcoal-400 mb-1">طريقة الدفع</p>
                  <p className="text-sm font-medium text-charcoal-700">{methodLabels[order.method_paid] || order.method_paid || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-charcoal-400 mb-1">الإجمالي</p>
                  <p className="text-lg font-bold text-charcoal-900">{order.total_cost} شيكل</p>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            {order.address && (
              <div className="bg-white rounded-2xl border border-cream-300 shadow-sm p-6">
                <h3 className="text-sm font-bold text-charcoal-900 mb-3">عنوان الشحن</h3>
                <div className="text-sm text-charcoal-600 space-y-1">
                  <p className="font-medium text-charcoal-900">{order.address.first_name} {order.address.last_name}</p>
                  <p>{order.address.address}</p>
                  <p>{order.address.city}{order.address.postcode ? ` - ${order.address.postcode}` : ''}</p>
                  <p className="text-xs" dir="ltr">{order.address.phone}</p>
                </div>
              </div>
            )}

            {/* Products */}
            <div className="bg-white rounded-2xl border border-cream-300 shadow-sm p-6">
              <h3 className="text-sm font-bold text-charcoal-900 mb-4">المنتجات ({order.order_items?.length || 0})</h3>
              <div className="space-y-3">
                {order.order_items?.map((item: any) => (
                  <div key={item.id} className="flex items-center gap-4 p-3 bg-cream-50 rounded-xl">
                    <div className="w-14 h-16 rounded-lg overflow-hidden bg-cream-200 flex-shrink-0">
                      {item.product?.images?.[0] ? (
                        <img src={storageUrl(item.product.images[0].image_path)} alt={item.product_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-charcoal-300"><i className="ri-image-line" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-charcoal-900 truncate">{item.product_name}</p>
                      {item.size && <p className="text-xs text-charcoal-400">المقاس: {item.size}</p>}
                      <p className="text-xs text-charcoal-500 mt-1">{item.quantity} × {item.price} شيكل</p>
                    </div>
                    <span className="text-sm font-bold text-charcoal-900">{item.total} شيكل</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-cream-200 mt-4 pt-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-charcoal-500">المجموع الفرعي</span><span>{order.total_cost - (order.shipping_cost || 0)} شيكل</span></div>
                <div className="flex justify-between text-sm"><span className="text-charcoal-500">الشحن</span><span className={order.shipping_cost > 0 ? '' : 'text-green-600'}>{order.shipping_cost > 0 ? `${order.shipping_cost} شيكل` : 'مجاني'}</span></div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-cream-200"><span>الإجمالي</span><span>{order.total_cost} شيكل</span></div>
              </div>
              </div>
              {(order.gift_card_amount > 0 || order.delivery_method) && (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5 text-xs text-charcoal-600 bg-cream-100 px-2.5 py-1 rounded-full">
                    <i className={order.delivery_method === 'pickup' ? 'ri-store-2-line' : 'ri-truck-line'} />
                    {order.delivery_method === 'pickup' ? 'استلام من المتجر' : 'توصيل'}
                  </div>
                  {order.gift_card_amount > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
                      <i className="ri-gift-line" />بطاقة هدية: {order.gift_card_amount} شيكل
                    </div>
                  )}
                  {order.remaining_amount > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
                      <i className="ri-money-dollar-circle-line" />متبقي: {order.remaining_amount} شيكل
                    </div>
                  )}
                </div>
              )}
            </div>
        )}
      </div>
    </main>
  );
}
